import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import TuneIcon from '@mui/icons-material/Tune';
import BugReportIcon from '@mui/icons-material/BugReport';
import SecurityIcon from '@mui/icons-material/Security';

// ── Version config ────────────────────────────────────────────────────────────
// Numri nuk shkruhet më këtu: del nga hyrja e parë e `RELEASES`, ndaj një
// version pa shënime nuk ekziston dot. Lidhja me `package.json` mbahet nga
// `updatesModalVersion.test.js`.
import { CURRENT_VERSION, RELEASES, isLatest } from '../../config/releases';
import { authFetch } from '../../services/apiServices';

// Ngjyrat dhe ikonat janë pamje, jo të dhëna — prandaj rrinë këtu, kurse
// `config/releases.js` mban vetëm tekstin e lëshimeve.
const TYPES = {
  new:      { label: 'I ri',        color: '#7C3AED', bg: '#F5F3FF', icon: <FiberNewIcon sx={{ fontSize: 13 }} /> },
  improve:  { label: 'Përmirësim',  color: '#0284C7', bg: '#EFF6FF', icon: <TuneIcon sx={{ fontSize: 13 }} /> },
  fix:      { label: 'Korrigjim',   color: '#16A34A', bg: '#F0FDF4', icon: <BugReportIcon sx={{ fontSize: 13 }} /> },
  security: { label: 'Siguri',      color: '#DC2626', bg: '#FEF2F2', icon: <SecurityIcon sx={{ fontSize: 13 }} /> },
};

export { CURRENT_VERSION };
/** Sa ndryshime mban i gjithë changelog-u — numër i saktë, jo «233+». */
const totalEntries = RELEASES.reduce(
  (n, r) => n + r.sections.reduce((m, sec) => m + sec.entries.length, 0), 0);

const storageKey = (userId) => `nexuma_seen_v${CURRENT_VERSION}_u${userId || 'guest'}`;

/**
 * A e ka parë ky përdorues versionin e tanishëm?
 *
 * `localStorage` është kujtesa e SHPEJTË — lexohet para se faqja të vizatohet,
 * ndaj dritarja nuk pulson. Por ajo i takon shfletuesit: një pastrim cache-je,
 * një dritare private ose një kompjuter tjetër, dhe e njëjta dritare kthehej
 * çdo herë. E vërteta rri te serveri (`users.seen_version`), dhe ajo kontrollohet
 * menjëherë pas hyrjes — shih `syncSeenFromServer`.
 */
export const hasSeenCurrentVersion = (userId) => {
  try { return localStorage.getItem(storageKey(userId)) === '1'; } catch { return false; }
};

export const markVersionSeen = (userId) => {
  try { localStorage.setItem(storageKey(userId), '1'); } catch { /* dritare private */ }
  // Serveri mban kujtesën e vërtetë; dështimi këtu nuk e prish mbylljen.
  authFetch('/api/users/seen-version', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ version: CURRENT_VERSION }),
  }).catch(() => {});
};

/**
 * Sjell kujtesën e serverit te ky shfletues.
 *
 * Kthen `true` kur versioni është parë tashmë — atëherë dritarja nuk hapet fare,
 * edhe pse `localStorage` këtu është bosh. Pa lidhje me serverin, kthen `null`
 * dhe vendimi i mbetet kujtesës lokale, pikërisht si më parë.
 */
export async function syncSeenFromServer(userId) {
  try {
    const r = await authFetch('/api/users/seen-version').then((x) => x.json());
    if (!r?.success) return null;
    const seen = r.data?.seenVersion === CURRENT_VERSION;
    if (seen) { try { localStorage.setItem(storageKey(userId), '1'); } catch { /* s'ka ku */ } }
    return seen;
  } catch {
    return null;
  }
}



// ── Entry row ─────────────────────────────────────────────────────────────────
function EntryRow({ type, text }) {
  const t = TYPES[type] || TYPES.new;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.65 }}>
      <Box sx={{
        minWidth: 22, height: 22, borderRadius: '6px',
        bgcolor: t.bg, color: t.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, mt: '1px',
      }}>
        {t.icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip
            label={t.label}
            size="small"
            sx={{
              height: 15, fontSize: '0.58rem', fontWeight: 800,
              bgcolor: t.bg, color: t.color,
              '& .MuiChip-label': { px: 0.85, lineHeight: '15px' },
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: '0.79rem', color: '#334155', lineHeight: 1.55, fontWeight: 450 }}>
            {text}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ── Release panel ─────────────────────────────────────────────────────────────
function ReleasePanel({ release }) {
  const allEntries   = release.sections.flatMap(s => s.entries);
  const totalEntries = allEntries.length;
  const newCount     = allEntries.filter(e => e.type === 'new').length;
  const fixCount     = allEntries.filter(e => e.type === 'fix').length;
  return (
    <Box sx={{ pb: 1 }}>
      {release.sections.map((sec, si) => (
        <Box key={sec.title}>
          {si > 0 && <Divider sx={{ mx: 2.5, borderColor: '#F1F5F9' }} />}
          <Box sx={{ px: 2.5, pt: si === 0 ? 2 : 1.5, pb: 0.5 }}>
            <Typography sx={{ fontSize: '0.67rem', fontWeight: 800, color: '#94A3B8', letterSpacing: '0.07em', textTransform: 'uppercase' }}>
              {sec.title}
            </Typography>
          </Box>
          <Box sx={{ px: 2.5, pb: 0.5 }}>
            {sec.entries.map((entry, ei) => (
              <EntryRow key={ei} {...entry} />
            ))}
          </Box>
        </Box>
      ))}
      {/* Stats */}
      <Box sx={{ mx: 2.5, mt: 1.5, p: 1.5, bgcolor: '#F8FAFC', borderRadius: '10px', border: '1px solid #F1F5F9', display: 'flex', gap: 3 }}>
        {[
          { label: 'Gjithsej', value: totalEntries, color: '#475569' },
          { label: 'Veçori të reja', value: newCount, color: '#7C3AED' },
          { label: 'Korrigjime', value: fixCount, color: '#16A34A' },
        ].map(s => (
          <Box key={s.label}>
            <Typography sx={{ fontSize: '0.62rem', color: '#94A3B8', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              {s.label}
            </Typography>
            <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: s.color, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
              {s.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function UpdatesModal({ open, onClose, userId }) {
  const [tab, setTab] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!open) setDismissed(false);
  }, [open]);

  const handleContinue = () => {
    // Mbylle menjëherë në ekran, edhe kur shfletuesi s'lejon localStorage.
    setDismissed(true);
    onClose?.();
    try { markVersionSeen(userId); } catch (_) { /* mbyllja nuk varet nga storage */ }
  };

  return (
    <Dialog
      open={open && !dismissed}
      onClose={handleContinue}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(15,23,42,0.22)',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{
        background: 'linear-gradient(145deg, #0C1A2E 0%, #162840 55%, #1B3350 100%)',
        px: 3, pt: 2.5, pb: 2,
        flexShrink: 0, position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', top: -40, right: -40,
          width: 160, height: 160, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(62,143,163,0.22) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, position: 'relative' }}>
          <Box sx={{
            width: 46, height: 46, borderRadius: '13px',
            background: 'linear-gradient(135deg, #3E8FA3 0%, #256878 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, boxShadow: '0 6px 18px rgba(62,143,163,0.45)',
          }}>
            <AutoAwesomeIcon sx={{ fontSize: 23, color: '#fff' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: '#fff', letterSpacing: '-0.03em', lineHeight: 1 }}>
                Përditësimet e Sistemit
              </Typography>
              {tab === 0 && (
                <Chip label="I fundit" size="small" sx={{
                  height: 17, fontSize: '0.58rem', fontWeight: 800,
                  bgcolor: 'rgba(62,143,163,0.28)', color: '#7DD3E8',
                  border: '1px solid rgba(62,143,163,0.45)',
                  '& .MuiChip-label': { px: 1 },
                }} />
              )}
            </Box>
            <Typography noWrap sx={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.58)', fontWeight: 500, letterSpacing: '-0.01em' }}>
              {RELEASES[tab]?.label || `${RELEASES.length} versione · ${totalEntries} ndryshime`}
            </Typography>
          </Box>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px', px: 1.25, py: 0.6, flexShrink: 0,
          }}>
            <Typography sx={{ fontSize: '0.71rem', fontWeight: 800, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.05em' }}>
              {RELEASES[tab]?.version || RELEASES[0].version}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Tabs ── */}
      <Box sx={{ borderBottom: '1px solid #F1F5F9', flexShrink: 0, bgcolor: '#fff' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40, px: 1.5,
            '& .MuiTab-root': {
              minHeight: 40, fontSize: '0.76rem', fontWeight: 600,
              textTransform: 'none', letterSpacing: '-0.01em',
              color: '#94A3B8', px: 1.5, py: 0,
              '&.Mui-selected': { color: '#0F172A', fontWeight: 800 },
            },
            '& .MuiTabs-indicator': { bgcolor: '#3E8FA3', height: 2.5, borderRadius: '2px 2px 0 0' },
          }}
        >
          {RELEASES.map((r) => (
            <Tab
              key={r.version}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {r.version}
                  {isLatest(r) && (
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#3E8FA3' }} />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* ── Content ── */}
      <DialogContent sx={{
        p: 0, flex: 1, minHeight: 0, overflowY: 'auto',
        '&::-webkit-scrollbar': { width: 5 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#E2E8F0', borderRadius: 3 },
      }}>
        {RELEASES.map((release, ri) => (
          <Box key={release.version} hidden={tab !== ri}>
            {tab === ri && <ReleasePanel release={release} />}
          </Box>
        ))}
      </DialogContent>

      {/* ── Footer ── */}
      <Box sx={{
        px: 3, py: 1.75, borderTop: '1px solid #F1F5F9', bgcolor: '#FAFBFC',
        display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0,
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontWeight: 600 }}>
            v{CURRENT_VERSION} · {RELEASES.length} versione · {totalEntries} ndryshime
          </Typography>
          <Typography sx={{ fontSize: '0.67rem', color: '#CBD5E1', fontWeight: 400, mt: 0.25 }}>
            Ndryshimet hyjnë në fuqi automatikisht — nuk kërkohet reinstalim
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleContinue}
          disableElevation
          sx={{
            borderRadius: '10px', textTransform: 'none',
            fontWeight: 800, fontSize: '0.85rem',
            px: 3.5, py: 1,
            background: 'linear-gradient(135deg, #3E8FA3 0%, #256878 100%)',
            boxShadow: '0 4px 16px rgba(62,143,163,0.3)',
            letterSpacing: '-0.01em', flexShrink: 0,
            '&:hover': {
              background: 'linear-gradient(135deg, #256878 0%, #1a4f5e 100%)',
              boxShadow: '0 6px 20px rgba(62,143,163,0.42)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.18s',
          }}
        >
          Vazhdo →
        </Button>
      </Box>
    </Dialog>
  );
}
